<template>
	<div class="flex flex-col">
		<transition name="fade" mode="out-in" appear>
			<div class="overflow-y-auto max-h-full" v-if="slide == 1">
				<div class="bg-capgemini-green p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Security as an Enabler of Transformation</h3>
					<h2 class="text-lg font-bold">Phase 1: Build a Case for Security Transformation </h2>
				</div>
				<div class="p-4">
					<h4 class="font-bold text-lg mb-2">From the Burning Platform</h4>
					<ul class="ml-6 mb-4">
						<li class="mb-1 list-disc">By lowering the priority of security in DX and prioritizing change, you open your business up to substantial risks as well as additional cost.</li>
						<li class="mb-1 list-disc">The lack of dialog with security during the blueprinting stages will most likely cause frustration and holdups/rollbacks in the  implementation phase.</li>
						<li class="mb-1 list-disc">DX drives the deperemiterization of enterprise with cloud, IoT, and extended ecosystem supply chains, risking the initiative survival.</li>
					</ul>
					<h4 class="font-bold text-lg mb-2">&hellip;to the Promised Land</h4>
					<ul class="ml-6">
						<li class="mb-1 list-disc">Transformational initiatives backed by security have greater chances of delivering change and value for the enterprise.</li>
						<li class="mb-1 list-disc">Security implemented at the core of digital platform and processes accelerates agile implementations, reduces time-to-market, and increases the flexibility of entire operations.</li>
						<li class="mb-1 list-disc">Security-by-design as a strategic imperative in the long run will support rollout of new DX initiatives, and provide guidance and guardrails for innovation.</li>
					</ul>
				</div>
				<div class="px-2 mb-4">
					<div class="bg-gray-600 text-white p-2 font-bold text-center">
						Security is an essential baseline for the organization’s digital transformation strategy
					</div>
					<div class="flex">
						<div class="flex-1 p-3 border mr-1">Engage into communication with security and build a strategy to support cross-functional collaboration underpinned by business objectives.</div>
						<div class="flex-1 p-3 border mr-1">Treat security as an enabler and involve the security function earlier in the process to reduce risks and increase efficiencies in process.</div>
						<div class="flex-1 p-3 border">Assess DX objectives in terms of potential risks and work collaboratively on making the core of the transformation future-proof.</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 2">
				<div class="bg-capgemini-green p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Define a Blueprint for Success</h3>
					<h2 class="text-lg font-bold">Phase 2: Build the Framework for Secure Transformation</h2>
				</div>
				<div class="p-4">
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-1.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Alignment &amp; Coalition</h4>
							<p>Define and formalize the role security should play in all processes, functions, and initiatives within the enterprise DX strategy. Establish a use-case-driven engagement framework.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-2.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Communicate Change</h4>
							<p>Jointly communicate with all C-level and senior staff that security and the business are aligned and working towards a common business aim.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-3.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Ensure Security Engagement</h4>
							<p>Support security transformation within strategic initiatives through co-innovation and by establishing feedback loops and a "champions" network across DX and security domains.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ciso-4.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Innovation</h4>
							<p>Reposition (if required) or reinforce that security is essential to innovation and progress as a core strategic priority of the business. </p>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 3">
				<div class="bg-capgemini-green p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Build Security Change into Transformation</h3>
					<h2 class="text-lg font-bold">Phase 3: Enable Security to Support Continual Change</h2>
				</div>
				<div class="p-4">
					<div class="flex flex-wrap">
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Communicate Vision &amp; Advantage</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Clearly stated vision of DX for an enterprise should highlight the role of security as the provider of trust for both internal initiatives and third-party interactions. </p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Change Measures of Security</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Transformation alters traditional processes and architecture. The new deperimeterized environment requires altering the risk assessment framework, functional stack, and the model of interaction between security and DX.</p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Embed Security in the Business Process</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>IT governance for enterprises in transformation admittedly gets overly complex, which generates gaps. Reducing the risk and avoiding digital deadlock requires business-aware and -aligned security.</p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Evangelize Security</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Be the change you want to see — becoming chief security evangelist to spread the word and harness the security-aware behavior both inside and outside your organization.</p>
							</div>
						</div>
						<div class="w-full text-center">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">DEMONSTRATE PROGRESS</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Demonstrate progress through unrestricted innovation that maintains momentum and creates sticky change without restraints.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 4">
				<div class="bg-capgemini-green p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Security Change Must be Continuous</h3>
					<h2 class="text-lg font-bold">Phase 4: Empower Security to Continue to Evolve</h2>
				</div>
				<div class="pb-16 pt-4 relative">
					<div class="absolute inset-0 bg-gray-200 z-0">
						<img class="h-full mx-auto" src="../assets/icons/arrow-down.svg" alt="">
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Enabling Security Evolution
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">As an advocate of change your role is to support and guide the change across all enterprise domains. Security as a business enabler should underpin the change process.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Share agile best practices across security and technology domains to streamline and automate for evolving business demands and a dynamic threat landscape.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Involve security in shaping the digital strategy to give an impetus for its transformation in line with requirements of DX enterprise.</div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Embed Security as Business Enabler
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Embed security change in the processes of the business. Think about compliance as an example of how well some things are addressed and whether security meets this standard.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Encourage and mandate collaboration. Measure and target departments and individuals on collaborative working and on security involvement.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Focus collaboration on speed to market, agility, cost efficiency, and productivity — all balanced against risk — as the future security role.</div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Security Outcomes
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Support your security function moving to automation and selective outsourcing, allowing staff to focus on essential business outcomes.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Adding flexibility to the security function you change the supply chain of trust in the organization to seamless, timely, value-focused delivery.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Support the changing role of the CISO by providing access to broader resources and embedding security into DX strategy as an advisor and facilitator of change.</div>
						</div>
					</div>					
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 5">
				<div class="bg-capgemini-green p-4 leading-none">
					<h2 class="text-lg font-bold">Think of &hellip;</h2>
				</div>
				<div class="p-4">
					<ol class="ml-4">
						<li class="list-decimal leading-tight rounded mb-2">Putting digital trust as a cornerstone for building services, products, and initiatives</li>
						<li class="list-decimal leading-tight rounded mb-2">Supporting and advocating the automation and integration of security into processes across the organization</li>
						<li class="list-decimal leading-tight rounded mb-2">Supporting a collaborative spirit for organizationwide security transformation by empowering champions</li>
					</ol>
				</div>
				<div class="card">
					<div class="p-4">
						<h2 class="text-lg font-bold mb-4">Further info:</h2>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<button type="button" @click="showCovid()">
									<p class="p-1 mb-2 font-bold">How does Covid-19 affect Security?</p>									
								</button>
							</div>
						</div>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/wp-content/uploads/2019/01/The-Modern-Connected-CISO.pdf" target="_blank" @click="trackDownload">
									<p class="p-1 mb-2 font-bold">Download The Modern, Connected CISO InfoBrief</p>
									<div class="mb-2 p-2 border bg-white w-2/3">
										<img src="../assets/infobrief.png" alt="" class="">
									</div>
									
								</a>
							</div>
						</div>
						<div class="flex mt-4">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/cybersecurity" target="_blank" @click="trackVisit">
									<p class="p-1 font-bold">Find out more about Cybersecurity Services at capgemini.com</p>									
								</a>
							</div>
						</div>						
					</div>
				</div>
			</div>
		</transition>
		<slot name="control"></slot>
	</div>
</template>

<script>
	export default {

		name: 'TransformationSlide',
		props: ['slide', 'total'],
		data () {
			return {

			}
		},
		methods:{
			trackDownload: function(){
				this.$gtag.event('download', {
					'event_category': 'PDF',
					'event_label': 'Infobrief'
				})
			},
			trackVisit: function(){
				this.$gtag.event('click', {
					'event_category': 'Link',
					'event_label': 'https://www.capgemini.com/cybersecurity'
				})
			},
			showCovid: function(){
				this.$emit('showCovid');
			}
		}
	}
</script>

<style lang="css" scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to
/* .component-fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}
</style>